import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Separator } from '@/components/ui/separator';
import { useAddStudiesToFolder, useCreateFolder, useDeleteFolder, useFolderTree, useReorderFolder, useUpdateFolder, } from '@/hooks/use-study-folders';
import { toast } from '@/hooks/use-toast';
import { FolderPlus, Loader2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateFolderDialog, DeleteFolderDialog, RenameFolderDialog } from './CreateFolderDialog';
import { FolderTreeProvider } from './FolderTreeContext';
import { FolderTreeItem } from './FolderTreeItem';
import { MoveFolderDialog } from './MoveFolderDialog';
export function findFolderById(nodes, id) {
    for (const node of nodes) {
        if (node.id === id)
            return node;
        const found = findFolderById(node.children, id);
        if (found)
            return found;
    }
    return null;
}
function isDescendantOf(nodes, targetId, ancestorId) {
    const ancestor = findFolderById(nodes, ancestorId);
    if (!ancestor)
        return false;
    function searchDescendants(node) {
        for (const child of node.children) {
            if (child.id === targetId)
                return true;
            if (searchDescendants(child))
                return true;
        }
        return false;
    }
    return searchDescendants(ancestor);
}
function getSiblings(personal, clinic, parentFolderId, scope) {
    if (!parentFolderId) {
        return scope === 'USER' ? personal : clinic;
    }
    const allNodes = [...personal, ...clinic];
    const parent = findFolderById(allNodes, parentFolderId);
    return parent?.children ?? [];
}
function RootDropZone({ scope, onMoveToRoot }) {
    const [isOver, setIsOver] = useState(false);
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsOver(true);
    };
    const handleDragLeave = () => {
        setIsOver(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        setIsOver(false);
        const folderId = e.dataTransfer.getData('text/plain');
        if (folderId) {
            onMoveToRoot(folderId, scope);
        }
    };
    return (_jsx("div", { className: `mx-2 h-2 rounded transition-colors ${isOver ? 'bg-primary/20' : ''}`, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop }));
}
export function FolderTree({ activeFolderId, onFolderSelect, clinicId }) {
    const { t } = useTranslation();
    const { data: folderTree, isLoading, error } = useFolderTree();
    const createFolder = useCreateFolder();
    const updateFolder = useUpdateFolder();
    const deleteFolder = useDeleteFolder();
    const reorderFolder = useReorderFolder();
    const addStudiesToFolder = useAddStudiesToFolder();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [createParentFolder, setCreateParentFolder] = useState(null);
    const [createScope, setCreateScope] = useState('USER');
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [folderToRename, setFolderToRename] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [folderToDelete, setFolderToDelete] = useState(null);
    const [folderToMove, setFolderToMove] = useState(null);
    const handleCreateFolder = (name, scope, parentFolderId, clinicId) => {
        createFolder.mutate({ name, scope, clinicId, parentFolderId }, {
            onSuccess: () => {
                setCreateDialogOpen(false);
                setCreateParentFolder(null);
            },
        });
    };
    const handleRename = (folder) => {
        setFolderToRename(folder);
        setRenameDialogOpen(true);
    };
    const handleRenameSubmit = (name) => {
        if (!folderToRename)
            return;
        updateFolder.mutate({ id: folderToRename.id, request: { name } }, {
            onSuccess: () => {
                setRenameDialogOpen(false);
                setFolderToRename(null);
            },
        });
    };
    const handleDelete = (folder) => {
        setFolderToDelete(folder);
        setDeleteDialogOpen(true);
    };
    const handleDeleteConfirm = () => {
        if (!folderToDelete)
            return;
        deleteFolder.mutate(folderToDelete.id, {
            onSuccess: () => {
                setDeleteDialogOpen(false);
                setFolderToDelete(null);
                // Clear selection if deleted folder was selected
                if (activeFolderId === folderToDelete.id) {
                    onFolderSelect(null);
                }
            },
        });
    };
    const handleCreateSubfolder = (parentFolder) => {
        setCreateParentFolder(parentFolder);
        setCreateScope(parentFolder.scope);
        setCreateDialogOpen(true);
    };
    const handleMoveFolder = useCallback((draggedId, targetId, position) => {
        if (!folderTree)
            return;
        const { personal, clinic } = folderTree;
        const allNodes = [...personal, ...clinic];
        const draggedFolder = findFolderById(allNodes, draggedId);
        const targetFolder = findFolderById(allNodes, targetId);
        if (!draggedFolder || !targetFolder)
            return;
        // Check scope mismatch
        if (draggedFolder.scope !== targetFolder.scope) {
            toast({
                title: t('folder.cannotMove'),
                description: t('folder.cannotMoveBetween'),
                variant: 'destructive',
            });
            return;
        }
        // Check if trying to move into own descendant
        if (isDescendantOf(allNodes, targetId, draggedId)) {
            toast({
                title: t('folder.cannotMove'),
                description: t('folder.cannotMoveIntoSelf'),
                variant: 'destructive',
            });
            return;
        }
        const handleError = (error) => {
            const errorMessage = error instanceof Error ? error.message : t('folder.failedToMove');
            // Map backend error codes to user-friendly messages
            let description = errorMessage;
            if (errorMessage.includes('CycleDetected')) {
                description = t('folder.cannotMoveIntoSelf');
            }
            else if (errorMessage.includes('ParentScopeMismatch')) {
                description = t('folder.cannotMoveBetween');
            }
            else if (errorMessage.includes('Unauthorized')) {
                description = t('folder.noPermissionToMove');
            }
            toast({
                title: t('folder.failedToMove'),
                description,
                variant: 'destructive',
            });
        };
        if (position === 'inside') {
            // Nesting: change parent to target
            updateFolder.mutate({ id: draggedId, request: { parentFolderId: targetId } }, { onError: handleError });
        }
        else {
            // Reordering: before or after target
            const newParentId = targetFolder.parentFolderId;
            const siblings = getSiblings(personal, clinic, newParentId, targetFolder.scope);
            // Calculate new display order
            const targetIndex = siblings.findIndex((s) => s.id === targetId);
            if (targetIndex === -1)
                return;
            let newDisplayOrder;
            if (position === 'before') {
                const prevSibling = siblings[targetIndex - 1];
                newDisplayOrder = prevSibling
                    ? (prevSibling.displayOrder + targetFolder.displayOrder) / 2
                    : targetFolder.displayOrder - 1;
            }
            else {
                // after
                const nextSibling = siblings[targetIndex + 1];
                newDisplayOrder = nextSibling
                    ? (targetFolder.displayOrder + nextSibling.displayOrder) / 2
                    : targetFolder.displayOrder + 1;
            }
            // If parent changed, update parent first then reorder
            if (draggedFolder.parentFolderId !== newParentId) {
                updateFolder.mutate({ id: draggedId, request: { parentFolderId: newParentId ?? undefined } }, {
                    onSuccess: () => {
                        reorderFolder.mutate({ id: draggedId, request: { newDisplayOrder } }, { onError: handleError });
                    },
                    onError: handleError,
                });
            }
            else {
                reorderFolder.mutate({ id: draggedId, request: { newDisplayOrder } }, { onError: handleError });
            }
        }
    }, [folderTree, updateFolder, reorderFolder, t]);
    // "Move to…" from a row's actions menu: the same nesting a drop "inside" a
    // folder performs (scope and cycle checks included), picked from a list.
    const handleMoveToSubmit = (targetFolderId) => {
        if (!folderToMove)
            return;
        handleMoveFolder(folderToMove.id, targetFolderId, 'inside');
        setFolderToMove(null);
    };
    const handleStudyDrop = useCallback((studyId, folderId) => {
        if (!folderTree)
            return;
        const { personal, clinic } = folderTree;
        const allNodes = [...personal, ...clinic];
        const folder = findFolderById(allNodes, folderId);
        addStudiesToFolder.mutate({ folderId, request: { studyIds: [studyId] } }, {
            onSuccess: () => {
                toast({
                    title: t('folder.studyAddedToFolder'),
                    description: t('folder.studyAddedToName', { name: folder?.name ?? 'folder' }),
                });
            },
            onError: (error) => {
                toast({
                    title: t('folder.failedToAddStudy'),
                    description: error instanceof Error ? error.message : t('folder.failedToAddStudyDefault'),
                    variant: 'destructive',
                });
            },
        });
    }, [folderTree, addStudiesToFolder, t]);
    const handleMoveToRoot = useCallback((folderId, scope) => {
        if (!folderTree)
            return;
        const { personal, clinic } = folderTree;
        const allNodes = [...personal, ...clinic];
        const folder = findFolderById(allNodes, folderId);
        if (!folder)
            return;
        // Check scope mismatch
        if (folder.scope !== scope) {
            toast({
                title: t('folder.cannotMove'),
                description: t('folder.cannotMoveBetween'),
                variant: 'destructive',
            });
            return;
        }
        // Only move if folder has a parent (i.e., not already at root)
        if (!folder.parentFolderId)
            return;
        // Get root siblings for calculating display order
        const rootSiblings = scope === 'USER' ? personal : clinic;
        const maxDisplayOrder = rootSiblings.length > 0 ? Math.max(...rootSiblings.map((s) => s.displayOrder)) : 0;
        updateFolder.mutate({ id: folderId, request: { parentFolderId: undefined } }, {
            onSuccess: () => {
                reorderFolder.mutate({
                    id: folderId,
                    request: { newDisplayOrder: maxDisplayOrder + 1 },
                });
            },
            onError: (error) => {
                toast({
                    title: t('folder.failedToMove'),
                    description: error instanceof Error ? error.message : t('folder.failedToMove'),
                    variant: 'destructive',
                });
            },
        });
    }, [folderTree, updateFolder, reorderFolder, t]);
    if (isLoading) {
        return (_jsx("div", { className: "flex items-center justify-center py-4", children: _jsx(Loader2, { className: "text-muted-foreground h-4 w-4 animate-spin" }) }));
    }
    if (error) {
        return (_jsx("div", { className: "text-destructive px-3 py-2 text-sm", children: t('folder.failedToLoadFolders') }));
    }
    const { personal = [] } = folderTree ?? {};
    const clinic = (folderTree?.clinic ?? []).filter((f) => !clinicId || f.clinicId === clinicId);
    return (_jsxs(FolderTreeProvider, { activeFolderId: activeFolderId, onActiveFolderChange: onFolderSelect, folders: folderTree, children: [_jsxs("div", { className: "space-y-1", role: "tree", "aria-label": t('folder.folderTree'), "data-testid": "folder-tree", children: [personal.length > 0 && (_jsxs(_Fragment, { children: [_jsx("div", { className: "text-muted-foreground px-3 py-1 text-xs font-medium", children: t('folder.myFoldersHeader') }), personal.map((folder) => (_jsx(FolderTreeItem, { folder: folder, onRename: handleRename, onDelete: handleDelete, onCreateSubfolder: handleCreateSubfolder, onMoveFolder: handleMoveFolder, onMoveTo: setFolderToMove, onStudyDrop: handleStudyDrop }, folder.id))), _jsx(RootDropZone, { scope: "USER", onMoveToRoot: handleMoveToRoot })] })), clinic.length > 0 && (_jsxs(_Fragment, { children: [personal.length > 0 && _jsx(Separator, { className: "my-2" }), _jsx("div", { className: "text-muted-foreground px-3 py-1 text-xs font-medium", children: t('folder.sharedFoldersHeader') }), clinic.map((folder) => (_jsx(FolderTreeItem, { folder: folder, onRename: handleRename, onDelete: handleDelete, onCreateSubfolder: handleCreateSubfolder, onMoveFolder: handleMoveFolder, onMoveTo: setFolderToMove, onStudyDrop: handleStudyDrop }, folder.id))), _jsx(RootDropZone, { scope: "CLINIC", onMoveToRoot: handleMoveToRoot })] })), personal.length === 0 && clinic.length === 0 && (_jsxs("div", { className: "flex flex-col items-center justify-center px-3 py-6 text-center", "data-testid": "folders-empty-state", children: [_jsx(FolderPlus, { className: "text-muted-foreground/50 mb-2 h-8 w-8" }), _jsx("p", { className: "text-muted-foreground text-sm", children: t('folder.noFoldersYet') }), _jsx("p", { className: "text-muted-foreground/70 mt-1 text-xs", children: t('folder.clickToCreate') })] }))] }), _jsx(CreateFolderDialog, { open: createDialogOpen, onOpenChange: setCreateDialogOpen, onSubmit: handleCreateFolder, parentFolder: createParentFolder, defaultScope: createScope, isLoading: createFolder.isPending, folders: folderTree ?? { personal: [], clinic: [] } }), _jsx(RenameFolderDialog, { open: renameDialogOpen, onOpenChange: setRenameDialogOpen, onSubmit: handleRenameSubmit, folder: folderToRename, isLoading: updateFolder.isPending }), _jsx(MoveFolderDialog, { open: folderToMove !== null, onOpenChange: (open) => !open && setFolderToMove(null), folder: folderToMove, folders: folderTree ?? { personal: [], clinic: [] }, onSubmit: handleMoveToSubmit }), _jsx(DeleteFolderDialog, { open: deleteDialogOpen, onOpenChange: setDeleteDialogOpen, onConfirm: handleDeleteConfirm, folder: folderToDelete, isLoading: deleteFolder.isPending })] }));
}
